/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame, LightState as LightMode } from "../../../shared/contracts";
import type { IAnimatable } from "../../core/ianimatable";
import { Item } from "../../core/item";
import type { Player } from "../../core/player";
import type { Table } from "../../table-model";
import { LightAnimation } from "./light-animation";
import type { LightData } from "./light-data";
import { LightState } from "./light-state";

/**
 * A light (an insert on the playfield or a bulb). It has no hit shape.
 */
export class Light extends Item<LightData> implements IAnimatable {
  private readonly state: LightState;
  private animation?: LightAnimation;

  public constructor(data: LightData) {
    super(data);
    this.state = new LightState(this.getName(), data.intensity, data.isVisible);
  }

  public getState(): LightState {
    return this.state;
  }

  public setupPlayer(_player?: Player, _table?: Table): void {
    this.animation = new LightAnimation(this.data, this.state);
    this.animation.updateIntensity();
  }

  public getAnimation(): LightAnimation {
    return this.animation!;
  }

  /** Sets the light to off (0), on (1) or blinking (2). `timeMs` is the game time. */
  public setMode(mode: LightMode, timeMs: number): void {
    this.animation!.setState(mode, timeMs);
  }

  public toFrame(): ItemFrame {
    return this.state.toFrame();
  }
}
